"use client";
import type { ReactNode } from "react";
import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import type { Customer } from "cy-api/simple-payments/customer/models";
import type { Nil } from "cy-util";
import { HeaderContextProvider } from "./HeaderContext";

/**
 * MainPageLayout is a layout component that provides a consistent layout for the main page of the application.
 * It includes a header, a sidebar, and a content area.
 * The sidebar is a fixed sidebar that is always visible on the left side of the screen.
 * The content area is where the main content of the page is displayed.
 */
export function MainPageLayout({
  children,
  drawer,
  sidebar,
  layoutFrame,
}: {
  children: ReactNode;
  customer?: Customer | Nil;
  sidebar: ({ closeSidebar }: { closeSidebar: () => void }) => ReactNode;
  drawer: ({ closeSidebar }: { closeSidebar: () => void }) => ReactNode;
  layoutFrame: ({
    children,
    openSidebar,
  }: {
    children: ReactNode;
    openSidebar: () => void;
  }) => ReactNode;
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  function closeSidebar() {
    setSidebarOpen(false);
  }
  return (
    <>
      {/* This requires updating your template:

        ```
        <html class="h-full bg-white">
        <body class="h-full">
        ```
      */}
      <HeaderContextProvider>
        <div className="container mx-auto max-w-[70rem]">
          <Transition.Root as={Fragment} show={sidebarOpen}>
            <Dialog
              as="div"
              className="relative z-50 lg:hidden"
              onClose={setSidebarOpen}
            >
              <Transition.Child
                as={Fragment}
                enter="transition-opacity ease-linear duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-gray-900/80" />
              </Transition.Child>

              <div className="fixed inset-0 flex">
                <Transition.Child
                  as={Fragment}
                  enter="transition ease-in-out duration-300 transform"
                  enterFrom="-translate-x-full"
                  enterTo="translate-x-0"
                  leave="transition ease-in-out duration-300 transform"
                  leaveFrom="translate-x-0"
                  leaveTo="-translate-x-full"
                >
                  <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                    <Transition.Child
                      as={Fragment}
                      enter="ease-in-out duration-300"
                      enterFrom="opacity-0"
                      enterTo="opacity-100"
                      leave="ease-in-out duration-300"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <div className="absolute top-0 left-full flex w-16 justify-center pt-5">
                        <button
                          className="-m-2.5 p-2.5"
                          onClick={() => {
                            setSidebarOpen(false);
                          }}
                          type="button"
                        >
                          <span className="sr-only">Close sidebar</span>
                          <XMarkIcon
                            aria-hidden="true"
                            className="h-6 w-6 text-white"
                          />
                        </button>
                      </div>
                    </Transition.Child>
                    {/* Sidebar component, swap this element with another sidebar if you like */}
                    {drawer({ closeSidebar })}
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition.Root>

          {/* Static sidebar for desktop */}
          {sidebar({ closeSidebar })}
          {/* Top Menu */}
          {layoutFrame({ children, openSidebar: () => setSidebarOpen(true) })}
        </div>
      </HeaderContextProvider>
    </>
  );
}
